import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import { api } from '../services/api';
import { Order, OrderStatus } from '../types';

const ORDER_STATUSES: { value: OrderStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_release', label: 'Pending Release' },
  { value: 'approved', label: 'Approved' },
  { value: 'in_production', label: 'In Production' },
  { value: 'ready_to_ship', label: 'Ready to Ship' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

interface OrderStats {
  total: number;
  drafts: number;
  pending_payment: number;
  submitted: number;
  in_production: number;
  shipped: number;
  delivered: number;
  needsQbEntry?: number;
  needsQbAdjustment?: number;
}

export default function OrderOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<OrderStats>({ total: 0, drafts: 0, pending_payment: 0, submitted: 0, in_production: 0, shipped: 0, delivered: 0, needsQbEntry: 0, needsQbAdjustment: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>(
    (searchParams.get('status') as OrderStatus) || ''
  );
  const [paidFilter, setPaidFilter] = useState<string>(searchParams.get('paid') || '');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>(searchParams.get('paymentMethod') || '');
  const [pendingAchFilter, setPendingAchFilter] = useState<boolean>(searchParams.get('pendingAch') === 'true');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [correctionOrders, setCorrectionOrders] = useState<any[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [qbFilter, setQbFilter] = useState<string>(searchParams.get('qb') || '');

  // Read URL params on mount
  useEffect(() => {
    const status = searchParams.get('status');
    if (status) setStatusFilter(status as OrderStatus);
    const paid = searchParams.get('paid');
    if (paid) setPaidFilter(paid);
    const pm = searchParams.get('paymentMethod');
    if (pm) setPaymentMethodFilter(pm);
    setPendingAchFilter(searchParams.get('pendingAch') === 'true');
  }, []);

  useEffect(() => {
    loadOrders();
  }, [statusFilter, paidFilter, paymentMethodFilter, pendingAchFilter, qbFilter, page, showArchived, debouncedSearch]);

  // Reset to page 1 whenever a filter changes, so the user never lands on an empty page
  useEffect(() => {
    setPage(1);
  }, [statusFilter, paidFilter, paymentMethodFilter, pendingAchFilter, qbFilter, showArchived, debouncedSearch]);

  // Debounce the search box so we hit the server (which searches ALL orders by
  // order#, sidemark, PO, and company) instead of only filtering the current page.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const mapOrder = (order: any) => ({
    ...order,
    dealerName: order.dealerName || order.companyName || order.company_name || 'Unknown Dealer',
    total: order.total || order.totalAmount || order.total_amount || 0,
    orderNumber: order.orderNumber || order.order_number,
    createdAt: order.createdAt || order.created_at,
    updatedAt: order.updatedAt || order.updated_at,
    shippedAt: order.shippedAt || order.shipped_at,
    deliveredAt: order.deliveredAt || order.delivered_at,
    trackingNumber: order.trackingNumber || order.tracking_number,
  });

  const loadOrders = async () => {
    try {
      setLoading(true);
      // Handle comma-separated status from URL (e.g. ?status=submitted,pending_payment)
      const statusParam = statusFilter.includes(',') ? statusFilter : (statusFilter || undefined);
      const response = await api.orders.getOrders({
        status: statusParam,
        page,
        limit: 20,
        includeArchived: showArchived || undefined,
        qbFilter: qbFilter || undefined,
        paidFilter: paidFilter || undefined,
        paymentMethod: paymentMethodFilter || undefined,
        pendingAch: pendingAchFilter || undefined,
        search: debouncedSearch || undefined,
      } as any);
      const mappedOrders = (response.data || response.items || []).map(mapOrder);

      setOrders(mappedOrders);
      setTotalPages(response.totalPages || response.pagination?.totalPages || 1);

      // Track correction orders (only pre-production — once in production it's too late)
      const productionStatuses = ['in_production', 'ready_to_ship', 'shipped', 'delivered', 'closed', 'completed', 'installed', 'warranty_registered'];
      setCorrectionOrders(mappedOrders.filter((o: any) =>
        (o.invoiceCorrectionRequestedAt || o.invoice_correction_requested_at) &&
        !(o.invoiceCorrectionResolvedAt || o.invoice_correction_resolved_at) &&
        !productionStatuses.includes(o.status)
      ));

      // Use server-side stats (counts across ALL orders, not just current page)
      if (response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
      setOrders([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
    try {
      setActionLoading(orderId);
      await api.orders.updateOrderStatus(orderId, newStatus);
      await loadOrders();
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (orderId: string, orderNumber: string) => {
    if (!confirm(`Archive order ${orderNumber}? It will be hidden from the default view.`)) return;
    setActionLoading(orderId);
    try {
      await api.orders.archiveOrder(orderId);
      await loadOrders();
    } catch (err) {
      console.error('Failed to archive:', err);
      alert('Failed to archive order');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnarchive = async (orderId: string) => {
    setActionLoading(orderId);
    try {
      await api.orders.unarchiveOrder(orderId);
      await loadOrders();
    } catch (err) {
      console.error('Failed to unarchive:', err);
      alert('Failed to unarchive order');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleQB = async (orderId: string, currentState: boolean) => {
    try {
      setActionLoading(orderId);
      await api.orders.markSentToQuickbooks(orderId, !currentState);
      // Refetch so the "Needs QB" stat card decrements; previously only the
      // row icon flipped and the header count stayed stale.
      await loadOrders();
    } catch (err: any) {
      console.error('Failed to update QB status:', err);
      // Surface the failure — previously this was swallowed, so a denied or
      // failed toggle just looked like "nothing happened".
      alert(
        `Could not update QuickBooks status: ${err?.message || 'Unknown error'}.\n\n` +
        `If this keeps happening, log out and back in — your session may predate a permissions change.`
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleAcknowledgeAdjustment = async (orderId: string) => {
    try {
      setActionLoading(orderId);
      await api.orders.acknowledgeInvoiceChange(orderId);
      await loadOrders();
    } catch (err) {
      console.error('Failed to acknowledge QB adjustment:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (orderId: string, orderNumber: string) => {
    if (!confirm(`Permanently delete order ${orderNumber}? This cannot be undone.`)) return;
    setActionLoading(orderId);
    try {
      await api.orders.deleteOrder(orderId);
      await loadOrders();
    } catch (err: any) {
      console.error('Failed to delete:', err);
      alert(err.message || 'Failed to delete order. Only draft and pending payment orders can be deleted.');
    } finally {
      setActionLoading(null);
    }
  };

  // Server already applied the search (across all orders/pages); no client filter.
  const filteredOrders = orders;

  return (
    <div>
      <PageHeader
        title="All Orders"
        subtitle="View all orders across all statuses"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </Card>
        <Card className="text-center border-l-4 border-gray-400">
          <p className="text-2xl font-bold text-gray-600">{stats.drafts}</p>
          <p className="text-sm text-gray-500">Drafts</p>
        </Card>
        <Card className="text-center border-l-4 border-yellow-400">
          <p className="text-2xl font-bold text-yellow-600">{stats.pending_payment}</p>
          <p className="text-sm text-gray-500">Pending Pay</p>
        </Card>
        <Card className="text-center border-l-4 border-blue-400">
          <p className="text-2xl font-bold text-blue-600">{stats.submitted}</p>
          <p className="text-sm text-gray-500">Submitted</p>
        </Card>
        <Card className="text-center border-l-4 border-green-400">
          <p className="text-2xl font-bold text-green-600">{stats.in_production}</p>
          <p className="text-sm text-gray-500">In Production</p>
        </Card>
        <Card className="text-center border-l-4 border-purple-400">
          <p className="text-2xl font-bold text-purple-600">{stats.shipped}</p>
          <p className="text-sm text-gray-500">Shipped</p>
        </Card>
        <Card className="text-center border-l-4 border-emerald-400">
          <p className="text-2xl font-bold text-emerald-600">{stats.delivered}</p>
          <p className="text-sm text-gray-500">Delivered</p>
        </Card>
        <div
          onClick={() => setQbFilter(qbFilter === 'needs' ? '' : 'needs')}
          className={`bg-white rounded-xl border-2 p-6 text-center cursor-pointer transition-colors border-l-4 ${
            (stats.needsQbEntry || 0) + (stats.needsQbAdjustment || 0) > 0
              ? 'border-red-500 bg-red-50 hover:bg-red-100'
              : 'border-green-400 bg-green-50 hover:bg-green-100'
          }`}
        >
          <p className={`text-2xl font-bold ${(stats.needsQbEntry || 0) + (stats.needsQbAdjustment || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {(stats.needsQbEntry || 0) + (stats.needsQbAdjustment || 0)}
          </p>
          <p className={`text-sm font-medium ${(stats.needsQbEntry || 0) + (stats.needsQbAdjustment || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
            Needs QB
          </p>
        </div>
      </div>

      {/* Correction Requests Alert */}
      {correctionOrders.length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800">
                {correctionOrders.length} Invoice Correction{correctionOrders.length > 1 ? 's' : ''} {correctionOrders.length > 1 ? 'Need' : 'Needs'} Attention
              </h3>
              <div className="mt-2 space-y-1">
                {correctionOrders.slice(0, 5).map((co: any) => (
                  <Link
                    key={co.id}
                    to={`/orders/${co.id}`}
                    className="block text-sm text-red-700 hover:text-red-900 hover:underline"
                  >
                    {co.orderNumber || co.order_number} — {co.dealerName} — {co.invoiceCorrectionMessage || co.invoice_correction_message || 'View details'}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as OrderStatus | '')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {ORDER_STATUSES.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Show Archived
          </label>
          <div className="flex gap-1">
            <button
              onClick={() => setQbFilter(qbFilter === 'needs' ? '' : 'needs')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                qbFilter === 'needs' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
              }`}
            >
              Needs QB
            </button>
            <button
              onClick={() => setQbFilter(qbFilter === 'done' ? '' : 'done')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                qbFilter === 'done' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
              }`}
            >
              QB Done
            </button>
          </div>
        </div>

        {/* Active filter banner */}
        {(paidFilter || paymentMethodFilter || pendingAchFilter || qbFilter) && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="text-gray-500">Filtered by:</span>
            {paidFilter === 'false' && <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Unpaid</span>}
            {paidFilter === 'outstanding' && <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Outstanding Balance</span>}
            {paidFilter === 'partial' && <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Partial Payment</span>}
            {paymentMethodFilter && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">{paymentMethodFilter.replace('_', ' ')}</span>}
            {pendingAchFilter && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">ACH Pending Deposit</span>}
            {qbFilter === 'needs' && <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Needs QB (Entry or Adjustment)</span>}
            {qbFilter === 'done' && <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">QB Done</span>}
            <button
              onClick={() => { setPaidFilter(''); setPaymentMethodFilter(''); setPendingAchFilter(false); setStatusFilter(''); setQbFilter(''); setSearchParams({}); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline ml-2"
            >
              Clear filters
            </button>
          </div>
        )}
      </Card>

      {/* Orders Table */}
      <Card>
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                      QB
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dealer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sidemark
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-lg font-medium">No orders found</p>
                          <p className="text-sm mt-1">
                            {statusFilter ? `No orders with status "${statusFilter}"` : 'No orders in the system yet'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {filteredOrders.map(order => {
                    const hasCorrectionRequest = !!((order as any).invoiceCorrectionRequestedAt || (order as any).invoice_correction_requested_at);
                    const correctionResolved = !!((order as any).invoiceCorrectionResolvedAt || (order as any).invoice_correction_resolved_at);
                    const inProduction = ['in_production', 'ready_to_ship', 'shipped', 'delivered', 'closed', 'completed', 'installed', 'warranty_registered'].includes(order.status);
                    const correctionPending = hasCorrectionRequest && !correctionResolved && !inProduction;
                    const isShippedOrDelivered = order.status === 'shipped' || order.status === 'delivered';
                    return (
                    <tr key={order.id} className={correctionPending ? 'bg-red-50 border-l-4 border-l-red-500' : 'hover:bg-gray-50'}>
                      <td className="px-3 py-4 text-center">
                        {(() => {
                          const sentToQb = !!((order as any).sentToQuickbooks || (order as any).sent_to_quickbooks);
                          const needsAdjust = !!((order as any).qboNeedsAdjustment || (order as any).qbo_needs_adjustment);
                          const hasSomfy = !!((order as any).hasSomfy || (order as any).has_somfy);
                          // Only show the QB toggle once money has landed — Bobbi
                          // enters orders in QB after deposit/payment, never before.
                          const paymentAmount = parseFloat((order as any).paymentAmount || (order as any).payment_amount || '0');
                          const hasPayment = paymentAmount >= 0.01;
                          const showQbToggle = order.status !== 'draft' && hasPayment;
                          return (
                            <div className="flex flex-col items-center gap-1">
                              {showQbToggle && (
                                <button
                                  onClick={() => handleToggleQB(order.id, sentToQb)}
                                  disabled={actionLoading === order.id}
                                  className="text-lg cursor-pointer"
                                  title={sentToQb ? 'QB Done — click to undo' : 'Not in QB — click to mark done'}
                                >
                                  {sentToQb
                                    ? <span className="text-green-600">&#10003;</span>
                                    : <span className="text-red-400">&#9711;</span>}
                                </button>
                              )}
                              {showQbToggle && needsAdjust && (
                                <button
                                  onClick={() => handleAcknowledgeAdjustment(order.id)}
                                  disabled={actionLoading === order.id}
                                  className="px-1.5 py-0.5 text-[9px] font-semibold rounded bg-amber-100 text-amber-700 hover:bg-amber-200 whitespace-nowrap"
                                  title={
                                    // Show WHY it needs adjusting. A bare "invoice changed"
                                    // leaves whoever picks this up to work out what moved —
                                    // and some of these are not guessable from the order alone
                                    // (e.g. a credit reallocated between two orders).
                                    ((order as any).qboAdjustmentReason || (order as any).qbo_adjustment_reason)
                                      ? `${(order as any).qboAdjustmentReason || (order as any).qbo_adjustment_reason}\n\nClick once you've updated the QuickBooks invoice.`
                                      : "Invoice changed after QB sync. Click after you've updated the QuickBooks invoice."
                                  }
                                >
                                  ADJUST → ✓
                                </button>
                              )}
                              {hasSomfy && (
                                <span
                                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-[9px] font-bold whitespace-nowrap"
                                  title="This order needs a Somfy motor or remote — make sure it's on hand or ordered"
                                >
                                  SMFY
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Link
                          to={`/orders/${order.id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {order.orderNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order.dealerName}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.sidemark || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <StatusBadge status={order.status} type="order" />
                          {(order as any).invoiceRequested && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700">
                              INV
                            </span>
                          )}
                          {(order as any).shortageHold && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700 flex items-center gap-0.5">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                              Parts Shortage
                            </span>
                          )}
                          {correctionPending && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700 flex items-center gap-0.5" title={(order as any).invoiceCorrectionMessage || (order as any).invoice_correction_message || 'Correction requested'}>
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                              Correction
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {(() => {
                          const total = Number(order.total || (order as any).totalAmount || (order as any).total_amount || 0);
                          const paid = parseFloat((order as any).paymentAmount || (order as any).payment_amount || '0');
                          const refunded = parseFloat((order as any).refundedAmount || (order as any).refunded_amount || '0');
                          const effPaid = paid - refunded;
                          const bal = Math.round((total - effPaid) * 100) / 100;
                          const paidInFull = order.paid_in_full || (order as any).paidInFull || (total > 0 && bal <= 0.01);
                          const depositPaid = order.deposit_paid || (order as any).depositPaid;
                          if (paidInFull) return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Paid</span>;
                          if (depositPaid || effPaid > 0.01) return <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700" title={`Paid $${effPaid.toLocaleString('en-US',{minimumFractionDigits:2})} of $${total.toLocaleString('en-US',{minimumFractionDigits:2})}`}>Partial</span>;
                          if (order.credit_approved || (order as any).creditApproved) return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Credit Approved</span>;
                          return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Pending</span>;
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const total = Number(order.total || (order as any).totalAmount || (order as any).total_amount || 0);
                          const paid = parseFloat((order as any).paymentAmount || (order as any).payment_amount || '0');
                          const refunded = parseFloat((order as any).refundedAmount || (order as any).refunded_amount || '0');
                          const bal = Math.round((total - (paid - refunded)) * 100) / 100;
                          return (
                            <div>
                              <div>${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                              {bal > 0.01 && order.status !== 'draft' && (
                                <div className="text-xs text-red-600 font-semibold">Bal: ${bal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          // Production-order sort key: shipped > deposit/payment > created.
                          // Justin sorts by this column to push orders into production
                          // in the order they were paid — `depositPaidAt` is "queue entry"
                          // time, not when the dealer first drafted the order.
                          const shippedAt = (order as any).shippedAt;
                          const depositPaidAt = (order as any).depositPaidAt || (order as any).deposit_paid_at;
                          const createdAt = order.createdAt;
                          if (isShippedOrDelivered && shippedAt) {
                            return (
                              <span title={`Created: ${format(new Date(createdAt), 'MMM d, yyyy')}${depositPaidAt ? ` · Paid: ${format(new Date(depositPaidAt), 'MMM d, yyyy')}` : ''}`}>
                                {format(new Date(shippedAt), 'MMM d, yyyy')}
                                <span className="text-xs text-gray-400 ml-1">(shipped)</span>
                              </span>
                            );
                          }
                          if (depositPaidAt) {
                            return (
                              <span title={`Created: ${format(new Date(createdAt), 'MMM d, yyyy')}`}>
                                {format(new Date(depositPaidAt), 'MMM d, yyyy')}
                                <span className="text-xs text-gray-400 ml-1">(paid)</span>
                              </span>
                            );
                          }
                          return format(new Date(createdAt), 'MMM d, yyyy');
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/orders/${order.id}`}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View
                          </Link>
                          {order.status === 'shipped' && (
                            <button
                              onClick={() => handleStatusChange(order.id, 'delivered' as OrderStatus)}
                              disabled={actionLoading === order.id}
                              className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {actionLoading === order.id ? '...' : 'Mark Delivered'}
                            </button>
                          )}
                          {(order as any).archivedAt ? (
                            <button
                              onClick={() => handleUnarchive(order.id)}
                              disabled={actionLoading === order.id}
                              className="text-green-600 hover:text-green-800 text-xs disabled:opacity-50"
                              title="Unarchive order"
                            >
                              {actionLoading === order.id ? '...' : 'Restore'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleArchive(order.id, order.orderNumber)}
                              disabled={actionLoading === order.id}
                              className="text-gray-400 hover:text-gray-600 text-xs disabled:opacity-50"
                              title="Archive order — hide from default view"
                            >
                              {actionLoading === order.id ? '...' : 'Archive'}
                            </button>
                          )}
                          {['draft', 'pending_payment', 'submitted', 'pending_release', 'cancelled', 'ready_to_ship'].includes(order.status) && (
                            <button
                              onClick={() => handleDelete(order.id, order.orderNumber)}
                              disabled={actionLoading === order.id}
                              className="text-red-400 hover:text-red-600 text-xs disabled:opacity-50"
                              title="Permanently delete order"
                            >
                              {actionLoading === order.id ? '...' : 'Delete'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
